/**
 * ATL-1633 — a supply-order line's `orderQuantity`, read and written as a NUMBER.
 *
 * `SupplyOrderDetailScreen.setItemOrderQuantity` keeps the quantity box's TEXT while the merchant
 * types (so "1." and "1.0" survive on the way to "1.05"), and its "still typing" test was "ends in
 * '.', ',' or '0'" — which is also true of 10, 240 and 500. `handleSave` then wrote that buffer
 * straight into Firestore (and the jsonb `supply_orders.items` copy), so every whole quantity ending
 * in zero was stored as a STRING. Product stocks summed it with `+`, and `0 + '240'` is `'0240'`:
 * the blue "(+N)" badge rendered +0240. Measured 2026-10-07: 79 items, 17 orders, 8 orgs, every one
 * a value ending in 0.
 *
 * The typing buffer stays a string — that is the screen's business — but nothing leaves it as one:
 * the save goes through {@link toSavedOrderItems}, and every reader goes through
 * {@link readOrderQuantity}, because the strings already written stay in the books until backfilled.
 */
import {parseDecimalAmount} from './decimalInput'

/**
 * The quantity a stored or in-progress line contributes: a finite number.
 *
 * An absent quantity is 0 (an item nobody ordered — the long-standing `|| 0`). A value that is
 * present but unreadable is ALSO 0 here, because this is the display/summing side and it must not
 * add text into a number; the save side ({@link toSavedOrderItems}) refuses such a value instead of
 * writing it.
 *
 * @param {unknown} raw
 * @returns {number}
 */
export const readOrderQuantity = (raw) => parseDecimalAmount(raw) ?? 0

/**
 * The lines a supply order SAVES: only positive quantities, each `orderQuantity` a number.
 *
 * A line whose box holds something that is not a plain decimal (a lone '.', a paste of '1.2.3') is
 * returned in `invalid` rather than silently saved as 0 or dropped — the caller refuses the save.
 *
 * @template T
 * @param {Array<T & {orderQuantity?: unknown}>} items
 * @returns {{items: Array<T & {orderQuantity: number}>, invalid: Array<T>}}
 */
export const toSavedOrderItems = (items) => {
  const saved = []
  const invalid = []
  for (const item of items || []) {
    const raw = item?.orderQuantity
    if (raw === undefined || raw === null || raw === '') continue
    const quantity = parseDecimalAmount(raw)
    if (quantity === null) {
      invalid.push(item)
      continue
    }
    if (quantity > 0) saved.push({...item, orderQuantity: quantity})
  }
  return {items: saved, invalid}
}
